import { createAsyncThunk, createEntityAdapter, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CmtsReduxThunkExtra, CmtsSerializableError } from "./thunkExtra.js";
import { readReduxString, serializeReduxError, serializeRepositoryFailure } from "./thunkExtra.js";

export type CmtsReportCatalogEntity = { readonly id: string; readonly code: string; readonly title: string; readonly permission?: string | undefined };
export type CmtsReportFilters = { readonly reportType?: string | undefined; readonly tenantCode?: string | undefined; readonly status?: string | undefined; readonly fromDate?: string | undefined; readonly toDate?: string | undefined };
export type CmtsReportExportMetadata = { readonly exportId: string; readonly reportType: string; readonly status: "queued" | "running" | "completed" | "failed"; readonly requestedAtUtc: string; readonly downloadUrl?: string | undefined };

const reportAdapter = createEntityAdapter<CmtsReportCatalogEntity>();

function toReportCatalogEntity(row: unknown): CmtsReportCatalogEntity {
  const id = readReduxString(row, ["id", "code", "reportCode"], "report");
  return {
    id,
    code: readReduxString(row, ["code", "reportCode"], id),
    title: readReduxString(row, ["title", "name", "label"], id),
    permission: readReduxString(row, ["permission", "permissionCode"], ""),
  };
}

export const fetchCmtsReportCatalog = createAsyncThunk<readonly CmtsReportCatalogEntity[], { readonly tenantId?: string | undefined } | undefined, { extra: CmtsReduxThunkExtra; rejectValue: CmtsSerializableError }>(
  "report/fetchCatalog",
  async (args, thunkApi) => {
    try {
      if (!thunkApi.extra.repositories) {
        return thunkApi.rejectWithValue({ message: "Repository suite is not available for report catalog refresh.", atUtc: new Date().toISOString() });
      }
      const result = await thunkApi.extra.repositories.reports.listCatalog(args?.tenantId);
      if (!result.ok) {
        return thunkApi.rejectWithValue(serializeRepositoryFailure(result.diagnostics, "Report catalog refresh failed."));
      }
      return result.data.map(toReportCatalogEntity);
    } catch (error: unknown) {
      return thunkApi.rejectWithValue(serializeReduxError(error, "Report catalog refresh failed."));
    }
  },
);


export type CmtsReportState = {
  readonly catalog: ReturnType<typeof reportAdapter.getInitialState>;
  readonly filters: CmtsReportFilters;
  readonly selectedReportType?: string | undefined;
  readonly exportMetadata?: CmtsReportExportMetadata | undefined;
  readonly downloadStatus: "idle" | "requesting" | "ready" | "failed";
  readonly loading: boolean;
  readonly error?: CmtsSerializableError | undefined;
};

const initialState: CmtsReportState = {
  catalog: reportAdapter.getInitialState(),
  filters: {},
  downloadStatus: "idle",
  loading: false,
};

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    reportCatalogLoaded(state, action: PayloadAction<readonly CmtsReportCatalogEntity[]>) {
      reportAdapter.setAll(state.catalog, action.payload);
      state.loading = false;
      state.error = undefined;
    },
    reportFiltersChanged(state, action: PayloadAction<CmtsReportFilters>) {
      state.filters = { ...state.filters, ...action.payload };
      state.selectedReportType = action.payload.reportType ?? state.selectedReportType;
    },
    reportSelected(state, action: PayloadAction<string | undefined>) {
      state.selectedReportType = action.payload;
    },
    reportExportRequested(state, action: PayloadAction<CmtsReportExportMetadata>) {
      state.exportMetadata = action.payload;
      state.downloadStatus = action.payload.status === "completed" ? "ready" : "requesting";
      state.error = undefined;
    },
    reportDownloadStatusChanged(state, action: PayloadAction<CmtsReportState["downloadStatus"]>) {
      state.downloadStatus = action.payload;
    },
    reportLoadStarted(state) {
      state.loading = true;
      state.error = undefined;
    },
    reportErrorRecorded(state, action: PayloadAction<CmtsSerializableError>) {
      state.loading = false;
      state.downloadStatus = "failed";
      state.error = action.payload;
    },
    reportCleared(state) {
      reportAdapter.removeAll(state.catalog);
      state.filters = {};
      state.selectedReportType = undefined;
      state.exportMetadata = undefined;
      state.downloadStatus = "idle";
      state.loading = false;
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCmtsReportCatalog.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchCmtsReportCatalog.fulfilled, (state, action) => {
        reportAdapter.setAll(state.catalog, action.payload);
        state.loading = false;
        state.error = undefined;
      })
      .addCase(fetchCmtsReportCatalog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? serializeReduxError(action.error, "Report catalog refresh failed.");
      });
  },
});

export const reportEntityAdapter = reportAdapter;
export const reportActions = reportSlice.actions;
export const reportReducer = reportSlice.reducer;
